import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, 
  Clock, 
  Users, 
  Target,
  Activity,
  BarChart3,
  CheckCircle,
  AlertTriangle,
  Filter,
  Download,
  RefreshCw,
  Search
} from 'lucide-react';
import { useToast } from '../../common/Toast';
import API_CONFIG from '../../../config/api';
import PageBreadcrumb from '../../common/PageBreadcrumb';
import PageHeader from '../../common/PageHeader';
import '../ProjectDashboardReportsStandalone.css';

const PerformanceReports = () => {
  const { success, error: showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState({
    totalProjects: 0,
    activeProjects: 0,
    averageProgress: 0,
    onTimeProjects: 0,
    delayedProjects: 0,
    teamPerformance: [],
    milestoneCompletion: []
  });

  useEffect(() => {
    loadReportData();
  }, []);

  const loadReportData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_CONFIG.ENDPOINTS.PROJECTS}/dashboard`, {
        headers: API_CONFIG.HEADERS
      });

      if (response.ok) {
        const data = await response.json();
        // Calculate performance metrics from dashboard data
        const totalProjects = data.totalProjects || 0;
        const activeProjects = data.activeProjects || 0;
        const averageProgress = data.averageProgress || 0;
        const budgetData = data.budgetData || [];
        
        // Calculate on-time vs delayed projects
        const onTimeProjects = budgetData.filter(p => p.progress >= 70).length;
        const delayedProjects = budgetData.filter(p => p.progress < 70).length;
        
        setReportData({
          totalProjects,
          activeProjects,
          averageProgress,
          onTimeProjects,
          delayedProjects,
          teamPerformance: data.teamPerformance || [],
          milestoneCompletion: data.upcomingMilestones || []
        });
      } else {
        setReportData(getMockData());
        showError('Failed to load performance data. Using sample data.');
      }
    } catch (err) {
      console.error('Error loading performance report data:', err);
      setReportData(getMockData());
      showError('Error loading performance report data. Using sample data.');
    } finally {
      setLoading(false);
    }
  };

  const getMockData = () => ({
    totalProjects: 24,
    activeProjects: 12,
    averageProgress: 68,
    onTimeProjects: 18,
    delayedProjects: 6,
    teamPerformance: [
      { name: 'Sarah Johnson', projects: 3, completion: 95, efficiency: 88, department: 'Engineering' },
      { name: 'Mike Chen', projects: 2, completion: 92, efficiency: 85, department: 'Engineering' },
      { name: 'Lisa Wang', projects: 4, completion: 89, efficiency: 82, department: 'Product' }
    ],
    milestoneCompletion: []
  });

  if (loading) {
    return (
      <div className="page-container">
        <PageBreadcrumb />
        <PageHeader title="Performance Reports" description="Project and team performance metrics" />
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading performance reports...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageBreadcrumb />
      <PageHeader 
        title="Performance Reports"
        description="Project and team performance metrics with detailed analytics"
      />
      <div className="page-container">
        {/* Summary Cards */}
        <div className="summary-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
          <div className="card h-100 page-kpi-card" style={{ margin: 0 }}>
            <div className="card-body page-kpi-body">
              <div className="page-kpi-content">
                <div className="page-kpi-text">
                  <h3 className="page-kpi-value color-1">{reportData.totalProjects}</h3>
                  <p className="page-kpi-label">Total Projects</p>
                </div>
                <div className="page-kpi-icon-container color-1">
                  <BarChart3 size={24} className="page-kpi-icon color-1" />
                </div>
              </div>
            </div>
          </div>
          <div className="card h-100 page-kpi-card" style={{ margin: 0 }}>
            <div className="card-body page-kpi-body">
              <div className="page-kpi-content">
                <div className="page-kpi-text">
                  <h3 className="page-kpi-value color-2">{reportData.averageProgress}%</h3>
                  <p className="page-kpi-label">Avg Progress</p>
                </div>
                <div className="page-kpi-icon-container color-2">
                  <TrendingUp size={24} className="page-kpi-icon color-2" />
                </div>
              </div>
            </div>
          </div>
          <div className="card h-100 page-kpi-card" style={{ margin: 0 }}>
            <div className="card-body page-kpi-body">
              <div className="page-kpi-content">
                <div className="page-kpi-text">
                  <h3 className="page-kpi-value color-3">{reportData.onTimeProjects}</h3>
                  <p className="page-kpi-label">On Time Projects</p>
                </div>
                <div className="page-kpi-icon-container color-3">
                  <CheckCircle size={24} className="page-kpi-icon color-3" />
                </div>
              </div>
            </div>
          </div>
          <div className="card h-100 page-kpi-card" style={{ margin: 0 }}>
            <div className="card-body page-kpi-body">
              <div className="page-kpi-content">
                <div className="page-kpi-text">
                  <h3 className="page-kpi-value color-4">{reportData.delayedProjects}</h3>
                  <p className="page-kpi-label">Delayed Projects</p>
                </div>
                <div className="page-kpi-icon-container color-4">
                  <AlertTriangle size={24} className="page-kpi-icon color-4" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Team Performance Table */}
        {reportData.teamPerformance && reportData.teamPerformance.length > 0 && (
          <div style={{ padding: '1.5rem 2rem', background: 'white', marginTop: '1.5rem' }}>
            <h3 style={{ marginBottom: '1rem', color: '#1f2937' }}>Team Performance</h3>
            <div className="table-header" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr' }}>
              <div className="table-header-cell">Team Member</div>
              <div className="table-header-cell">Projects</div>
              <div className="table-header-cell">Completion %</div>
              <div className="table-header-cell">Efficiency</div>
              <div className="table-header-cell">Department</div>
            </div>
            <div className="table-body">
              {reportData.teamPerformance.map((member, index) => (
                <div key={index} className="table-row" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr' }}>
                  <div className="table-cell">{member.name}</div>
                  <div className="table-cell">{member.projects}</div>
                  <div className="table-cell">
                    <div className="progress-container">
                      <div className="progress-bar">
                        <div className="progress-fill" style={{ width: `${member.completion}%` }}></div>
                      </div>
                      <span className="progress-text">{member.completion}%</span>
                    </div>
                  </div>
                  <div className="table-cell">{member.efficiency}%</div>
                  <div className="table-cell">{member.department}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default PerformanceReports;